import Img from "@assets/img";
import PopUp from "@components/PopUp";
import { RootState } from "@store/store";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import CONFIG from "src/config";
import { useDispatch } from "react-redux";
import { EAppAction } from "@store/appReducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { trackUserEvent } from "@utils/analytics";

const ReferFriendPopup = ({ isOpen }: { isOpen: boolean }) => {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);
  const appUrl = window.location.origin;
  const referLink = `${appUrl}/refer/${user.id}`;

  // Generate a referral code from user ID (you can modify this logic as needed)
  // const referralCode = `WNNR-${user.id?.toString().slice(-6).toUpperCase() || 'ABC123'}`;
  async function handleCopyLink() {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(referLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      // Track referral link copy
      trackUserEvent.referralSent();
    }
  }

  const onCloseReferPopup = () => {
    dispatch({
      type: EAppAction.SHOW_REFER_POPUP,
      payload: false,
    });
    setCopied(false);
  };

  // Mock referral count - replace with actual data from your API
  // const referralCount = 0;
  // const maxReferrals = 30;

  // Set popup background to transparent and hide title when component mounts
  useEffect(() => {
    const originalInfoColor = document.body.style.getPropertyValue('--info-color');
    document.body.style.setProperty('--info-color', 'transparent');

    // Add custom CSS to hide title and style popup
    const style = document.createElement('style');
    style.id = 'refer-popup-title-style';
    style.textContent = `
      div[role="dialog"] .header {
        display: none !important;
      }
      div[role="dialog"] > div {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
        max-height: 90vh !important;
        overflow: hidden !important;
      }
      div[role="dialog"] {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
        max-height: 90vh !important;
        overflow: hidden !important;
      }
      div[role="dialog"] .modal-container {
        padding: 0 !important;
        max-height: 90vh !important;
        overflow: hidden !important;
        background: transparent !important;
      }
      div[role="dialog"].height-default {
        max-height: 90vh !important;
        overflow: hidden !important;
        border: none !important;
        background: transparent !important;
      }
    `;
    document.head.appendChild(style);

    // Cleanup: restore original color and remove custom style when component unmounts
    return () => {
      if (originalInfoColor) {
        document.body.style.setProperty('--info-color', originalInfoColor);
      } else {
        document.body.style.removeProperty('--info-color');
      }
      const existingStyle = document.getElementById('refer-popup-title-style');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  return (
    <PopUp
      visible={isOpen}
      title=""
      onCancel={onCloseReferPopup}
      onOverlayClick={onCloseReferPopup}
    >
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">

          {/* Header with Gradient */}
          <div className="relative bg-gradient-to-br from-[#4B975B] via-[#5dad6d] to-[#4B975B] p-8 text-center overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
            </div>

            {/* Close Button */}
            <button
              onClick={onCloseReferPopup}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-white transition-colors shadow-lg"
            >
              <FontAwesomeIcon icon={['fas', 'xmark']} className="text-gray-700" />
            </button>

            {/* Gift Icon with Animation */}
            <div className="relative mb-4 animate-bounce-slow">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full">
                <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                </svg>
              </div>
            </div>

            <h2 className="relative text-3xl font-extrabold text-white mb-2">
              Refer & Earn! 🎁
            </h2>
            <p className="relative text-white/90 text-base font-medium">
              Get FREE raffle tickets for you and your friends!
            </p>
          </div>

          {/* Content */}
          <div className="p-8 space-y-6">
            {/* Main Message */}
            <div className="text-center space-y-2">
              <h3 className="text-2xl font-bold text-[#1f2c2b]">
                Share Your Link
              </h3>
              <p className="text-gray-600">
                Invite friends and both of you get rewarded with free raffle tickets!
              </p>
            </div>

            {/* Referral Link Box */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Your Referral Link
              </label>
              <div
                onClick={handleCopyLink}
                className="relative group cursor-pointer"
              >
                <div className="bg-gradient-to-br from-[#4B975B]/10 to-[#5dad6d]/10 border-2 border-[#4B975B]/30 rounded-xl p-4 transition-all duration-200 hover:border-[#4B975B] hover:shadow-lg">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-mono text-[#4B975B] font-semibold truncate flex-1">
                      {referLink}
                    </span>
                    <div className="flex-shrink-0 w-10 h-10 bg-[#4B975B] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Copy Feedback */}
                {copied && (
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-bounce">
                    ✓ Copied!
                  </div>
                )}
              </div>
            </div>

            {/* Share Buttons */}


            {/* Benefits Section */}

            {/* Disclaimer */}
            <div className="text-center">
              <p className="text-xs text-gray-500 leading-relaxed">
                Max 30 referrals per user/per month. Tickets will be credited when your referee signs in for the first time. *Terms and Conditions Apply.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PopUp>
  );
};

export default ReferFriendPopup;
